import type { RefObject } from 'react';

interface ExistingLabel {
  name: string;
  color: string;
}

interface LabelDropdownProps {
  dropdownRef: RefObject<HTMLDivElement | null>;
  position: { top: number; left: number; width: number };
  existingLabels: ExistingLabel[];
  labelColors: readonly string[] | string[];
  customLabelInput: string;
  newLabelColor: string;
  hoveredLabel: string | null;
  showDeleteButton: string | null;
  hoverTimeoutRef: RefObject<NodeJS.Timeout | null>;
  setCustomLabelInput: (value: string) => void;
  setNewLabelColor: (color: string) => void;
  setHoveredLabel: (name: string | null) => void;
  setShowDeleteButton: (name: string | null) => void;
  onSelectExisting: (label: ExistingLabel) => void;
  onCreateLabel: (name: string, color: string) => void;
  onDeleteLabel: (label: ExistingLabel) => void;
}

/**
 * Portal-positioned label dropdown for the Task Edit Modal: existing-labels grid
 * (hover-to-reveal delete), create-new input + color swatches.
 *
 * The orchestrator owns the click-outside useEffect and positioning effect; refs
 * and the create/select/delete side effects are passed in to keep behavior
 * identical. The previously triplicated create-label flow (Enter / + button /
 * swatch) collapses into the single `onCreateLabel(name, color)` handler.
 */
export function LabelDropdown({
  dropdownRef,
  position,
  existingLabels,
  labelColors,
  customLabelInput,
  newLabelColor,
  hoveredLabel: _hoveredLabel,
  showDeleteButton,
  hoverTimeoutRef,
  setCustomLabelInput,
  setNewLabelColor,
  setHoveredLabel,
  setShowDeleteButton,
  onSelectExisting,
  onCreateLabel,
  onDeleteLabel,
}: LabelDropdownProps) {
  return (
    <div
      ref={dropdownRef}
      className="fixed bg-white border border-gray-200 rounded-lg shadow-lg"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
        zIndex: 10001,
      }}
    >
      {/* Existing labels */}
      {existingLabels.length > 0 && (
              <div className="p-2">
                <div className="text-xs font-medium text-gray-500 mb-2 px-2">Existing Labels</div>
                <div className="flex flex-wrap gap-2 px-2">
                  {existingLabels.map((existingLabel) => (
                    <div
                      key={existingLabel.name}
                      className="relative"
                      onMouseEnter={() => {
                        setHoveredLabel(existingLabel.name);
                        if (hoverTimeoutRef.current) {
                          clearTimeout(hoverTimeoutRef.current);
                        }
                        hoverTimeoutRef.current = setTimeout(() => {
                          setShowDeleteButton(existingLabel.name);
                        }, 1500);
                      }}
                      onMouseLeave={() => {
                        setHoveredLabel(null);
                        setShowDeleteButton(null);
                        if (hoverTimeoutRef.current) {
                          clearTimeout(hoverTimeoutRef.current);
                        }
                      }}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onSelectExisting(existingLabel);
                        }}
                        className="px-2.5 py-1 rounded-md text-xs font-medium hover:opacity-80 transition-opacity"
                        style={{ backgroundColor: existingLabel.color, color: '#1f2937' }}
                      >
                        {existingLabel.name}
                      </button>
                      {showDeleteButton === existingLabel.name && (
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            await onDeleteLabel(existingLabel);
                            setShowDeleteButton(null);
                          }}
                          className="absolute -top-1 -right-1 w-4 h-4 bg-gray-400 hover:bg-gray-500 text-white rounded-full flex items-center justify-center text-xs leading-none"
                          title="Delete label"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {/* Separator line with margin */}
                <div className="border-b mx-2 mt-2"></div>
              </div>
            )}

            {/* Create new label */}
            <div className="p-2">
              <div className="text-xs font-medium text-gray-500 mb-2 px-2">Create New</div>
              <div className="space-y-2 px-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={customLabelInput}
                    onChange={(e) => setCustomLabelInput(e.target.value)}
                    placeholder="Label name"
                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                    style={{ background: 'var(--color-surface)' }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && customLabelInput.trim()) {
                        e.preventDefault();
                        e.stopPropagation();
                        await onCreateLabel(customLabelInput.trim(), newLabelColor);
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (customLabelInput.trim()) {
                        await onCreateLabel(customLabelInput.trim(), newLabelColor);
                      }
                    }}
                    className="text-gray-400 hover:text-gray-600 text-xl px-1"
                    title="Add label"
                  >
                    +
                  </button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {labelColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setNewLabelColor(color);
                        if (customLabelInput.trim()) {
                          await onCreateLabel(customLabelInput.trim(), color);
                        }
                      }}
                      className="w-6 h-6 rounded-full border-2 transition-all hover:scale-110"
                      style={{
                        backgroundColor: color,
                        borderColor: newLabelColor === color ? '#374151' : '#d1d5db',
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
    </div>
  );
}
